import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import moment from 'moment';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

const _openURL = (url) => {
  if (Platform.OS === 'web' && url) {
    window.open(url, '_blank');
  }
};

const DocsComponent = ({ title, listOfDoc = [], deleteFile }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [docsList, setDocsList] = useState(listOfDoc);
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  useEffect(() => {
    setDocsList(listOfDoc || []);
  }, [listOfDoc]);

  const fileCount = docsList.length;
  const folderDate = docsList.length > 0 && docsList[0]?.createdAt
    ? moment(docsList[0].createdAt).format('DD/MM/YYYY')
    : '23/06/2026';

  return (
    <View style={styles.folderCard}>
      {/* Folder Accordion Header */}
      <TouchableOpacity
        style={styles.folderHeader}
        onPress={() => setIsOpen(!isOpen)}
        activeOpacity={0.8}
      >
        <View style={styles.folderHeaderLeft}>
          <Ionicons
            name={isOpen ? "folder-open" : "folder"}
            size={20}
            color="#6C2BD9"
            style={{ marginRight: 10 }}
          />
          <Text style={styles.folderTitle}>{title || 'Property Folder'}</Text>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{fileCount} {fileCount === 1 ? 'file' : 'files'}</Text>
          </View>
        </View>

        <View style={styles.folderHeaderRight}>
          <Text style={styles.dateText}>{folderDate}</Text>
          <Feather
            name={isOpen ? "chevron-down" : "chevron-right"}
            size={18}
            color="#6B7280"
          />
        </View>
      </TouchableOpacity>

      {/* Accordion Content */}
      {isOpen && (
        <View style={styles.folderBody}>
          {docsList && docsList.length > 0 ? (
            docsList.map((file, index) => {
              const fileName = file?.document_name || file?.name || `Document ${index + 1}`;
              const fileDate = file?.createdAt ? moment(file.createdAt).format('DD/MM/YYYY') : '14/05/2026';
              const fileUrl = file?.document_url || file?.openbrixName;

              return (
                <View key={index} style={[styles.fileRow, isMobile && styles.fileRowMobile]}>
                  <View style={styles.fileLeft}>
                    <Ionicons name="document-text-outline" size={18} color="#6B7280" style={{ marginRight: 8 }} />
                    <TouchableOpacity onPress={() => _openURL(fileUrl)} activeOpacity={0.7} style={{ flex: 1 }}>
                      <Text style={styles.fileName} numberOfLines={1}>{fileName}</Text>
                    </TouchableOpacity>
                  </View>

                  <View style={styles.fileRight}>
                    <Text style={styles.fileMeta}>2.4 MB</Text>
                    <Text style={styles.fileDate}>{fileDate}</Text>

                    <View style={styles.actionsGroup}>
                      <TouchableOpacity
                        style={styles.iconBtn}
                        onPress={() => _openURL(fileUrl)}
                        activeOpacity={0.7}
                      >
                        <Feather name="download" size={15} color="#6C2BD9" />
                      </TouchableOpacity>

                      <TouchableOpacity
                        style={styles.iconBtn}
                        onPress={() => deleteFile(file.id || index)}
                        activeOpacity={0.7}
                      >
                        <Feather name="trash-2" size={15} color="#EF4444" />
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              );
            })
          ) : (
            <View style={styles.emptyWrap}>
              <Text style={styles.emptyText}>No documents in this folder.</Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

export default DocsComponent;

const styles = StyleSheet.create({
  folderCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    marginBottom: 10,
    overflow: 'hidden',
  },

  folderHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: '#FAFAFA',
  },

  folderHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  folderTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
    marginRight: 8,
  },
  badge: {
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  badgeText: {
    fontSize: 11,
    color: '#6B7280',
    fontWeight: '500',
  },

  folderHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dateText: {
    fontSize: 12,
    color: '#9CA3AF',
  },

  /* BODY */
  folderBody: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
    backgroundColor: '#FFFFFF',
  },

  fileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderColor: '#F9FAFB',
    gap: 12,
  },
  fileRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 6,
  },

  fileLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  fileName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },

  fileRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  fileMeta: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  fileDate: {
    fontSize: 12,
    color: '#6B7280',
  },

  actionsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  iconBtn: {
    padding: 6,
    borderRadius: 6,
    backgroundColor: '#F9FAFB',
  },

  emptyWrap: {
    paddingVertical: 14,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: '#9CA3AF',
  },
});
